import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { useSelector } from 'react-redux';
import axiosInstance from '../../../api/axios';
import { BASE_URL } from '../../../constants/apiConstants';
import { RootState } from '../../../store/store';
import {
  FormValues,
  MealTimeDetail,
  MenuProjectionItem,
  MenuRequisitionItem,
} from '../../../types/adminInterface';
import { toast } from "react-toastify";
import { clampValueNoForce, normalizeArray, normalizeValues, validateAndUpdateField } from '../../../utils/helper';

/**
 * useAdminConfiguration
 * ---------------------
 * This hook handles all state + logic for "Admin Configuration".
 */
const useAdminConfiguration = () => {
  const { params } = useSelector((state: RootState) => state.adminConfigurationParams);
  const userid = localStorage.getItem('userid');
  // ----------------------------------
  // Local States
  // ----------------------------------
  const [isPublishButtonDisabled, setIsPublishButtonDisabled] = useState(true);
  const [loading, setLoading] = useState(false);

  // Arrays from the API
  const [mealtime, setMealtime] = useState<MealTimeDetail[]>([]);
  const [menuProjectionData, setMenuProjectionData] = useState<MenuProjectionItem[]>([]);
  const [menuRequisitionData, setMenuRequisitionData] = useState<MenuRequisitionItem[]>([]);
  const [exceptionalMenuRequisitionData, setExceptionalMenuRequisitionData] = useState<MenuRequisitionItem[]>([]);
  const [kitchenIntentData, setKitchenIntentData] = useState<MenuRequisitionItem[]>([]);
  const [updateproductionplanmodel, setUpdateproductionplanmodel] = useState<MenuRequisitionItem[]>([]);

  // Site display options and selected id (from API)
  const [siteDisplayOptions, setSiteDisplayOptions] = useState<{
    itemdisplaymasterid: number;
    itemdisplayname: string;
  }[]>([]);
  const [selectedDisplayItemWiseId, setSelectedDisplayItemWiseId] = useState<number>(0);

  // Form values (initialized to 0)
  const [values, setValues] = useState<FormValues>({
    reviewtimeperiodmonths: 0,
    pastmonthprojection: 0,
    projectionquantitytolerancepercentage: 0,
    projectioncomparedays: 0,
    pastmonthrequisition: 0,
    requisitionquantitytolerancepercentage: 0,
    requisitioncomparedays: 0,
    pastmonthexceptionalrequisition: 0,
    exceptionalrequisitionquantitytolerancepercentage: 0,
    exceptionalrequisitioncomparedays: 0,
    pastmonthprojectiondashboard: 0,
    pastmonthkitchendashboard: 0,
    pastmonthsitemanagerdashboard: 0,
  });

  // ----------------------------------
  // Update Local States from API
  // ----------------------------------
  const updateLocalStatesFromApi = useCallback((newData: any) => {
    setValues((prev) => ({
      ...prev,
      // Initiate Menu Plan
      reviewtimeperiodmonths:
        newData?.initiatemenuplan?.reviewtimeperiodmonths ?? prev.reviewtimeperiodmonths,
      // Menu Projection
      pastmonthprojection:
        newData?.menuprojection?.pastmonthprojection ?? prev.pastmonthprojection,
      projectionquantitytolerancepercentage:
        newData?.menuprojection?.projectionquantitytolerancepercentage ??
        prev.projectionquantitytolerancepercentage,
      projectioncomparedays:
        newData?.menuprojection?.projectioncomparedays ?? prev.projectioncomparedays,
      // Menu Requisition
      pastmonthrequisition:
        newData?.menurequisitionmodel?.pastmonthrequisition ?? prev.pastmonthrequisition,
      requisitionquantitytolerancepercentage:
        newData?.menurequisitionmodel?.requisitionquantitytolerancepercentage ??
        prev.requisitionquantitytolerancepercentage,
      requisitioncomparedays:
        newData?.menurequisitionmodel?.requisitioncomparedays ?? prev.requisitioncomparedays,
      // Exceptional
      pastmonthexceptionalrequisition:
        newData?.exceptionalmenurequisitionmodel?.pastmonthexceptionalrequisition ??
        prev.pastmonthexceptionalrequisition,
      exceptionalrequisitionquantitytolerancepercentage:
        newData?.exceptionalmenurequisitionmodel
          ?.exceptionalrequisitionquantitytolerancepercentage ??
        prev.exceptionalrequisitionquantitytolerancepercentage,
      exceptionalrequisitioncomparedays:
        newData?.exceptionalmenurequisitionmodel?.exceptionalrequisitioncomparedays ??
        prev.exceptionalrequisitioncomparedays,
      // Dashboards
      pastmonthprojectiondashboard:
        newData?.menuprojectiondashboard?.pastmonthprojectiondashboard ??
        prev.pastmonthprojectiondashboard,
      pastmonthkitchendashboard:
        newData?.kitchendashboard?.pastmonthkitchendashboard ?? prev.pastmonthkitchendashboard,
      pastmonthsitemanagerdashboard:
        newData?.sitemanagerdashboard?.pastmonthsitemanagerdashboard ??
        prev.pastmonthsitemanagerdashboard,
    }));

    if (Array.isArray(newData?.initiatemenuplan?.mealtimedetailslist)) {
      setMealtime(newData.initiatemenuplan.mealtimedetailslist);
    }
    if (Array.isArray(newData?.menuprojection?.projectioncutoffrule)) {
      setMenuProjectionData(newData.menuprojection.projectioncutoffrule);
    }
    if (Array.isArray(newData?.menurequisitionmodel?.requisitioncutoffrule)) {
      setMenuRequisitionData(newData.menurequisitionmodel.requisitioncutoffrule);
    }
    if (Array.isArray(newData?.exceptionalmenurequisitionmodel?.exceptionalrequisitioncutoffrule)) {
      setExceptionalMenuRequisitionData(
        newData.exceptionalmenurequisitionmodel.exceptionalrequisitioncutoffrule
      );
    }
    if (Array.isArray(newData?.kitchenintentmodel?.kitchenintentcutoffrule)) {
      setKitchenIntentData(newData.kitchenintentmodel.kitchenintentcutoffrule);
    }
    if (Array.isArray(newData?.updateproductionplanmodel?.updateproductionplancutoffrule)) {
      setUpdateproductionplanmodel(
        newData.updateproductionplanmodel.updateproductionplancutoffrule
      );
    }
    // Site display configuration (API may return an array or an object with a nested list and selected id)
    // Primary check: expected shapes
    const sd = newData?.sitedisplayitemwisemodel;
    if (Array.isArray(sd) && sd.length > 0) {
      const first = sd[0];
      if (Array.isArray(first?.sitedisplayitemwisenew)) {
        setSiteDisplayOptions(first.sitedisplayitemwisenew);
      }
      setSelectedDisplayItemWiseId(first?.selecteddisplayitemwiseid ?? 0);
    } else if (sd && typeof sd === 'object') {
      // API returned an object like { sitedisplayitemwisenew: [...], selecteddisplayitemwiseid: 2 }
      if (Array.isArray(sd?.sitedisplayitemwisenew)) {
        setSiteDisplayOptions(sd.sitedisplayitemwisenew);
      }
      setSelectedDisplayItemWiseId(sd?.selecteddisplayitemwiseid ?? 0);
    } else {
      // Fallback: try to locate an array of objects that look like site-display options anywhere in newData
      const findSiteDisplayArray = (obj: any): any[] | null => {
        if (!obj || typeof obj !== 'object') return null;
        if (Array.isArray(obj)) {
          // Check if this array contains objects with itemdisplaymasterid or itemdisplayname
          if (obj.length > 0 && typeof obj[0] === 'object') {
            const firstObj = obj[0];
            if ('itemdisplaymasterid' in firstObj || 'itemdisplayname' in firstObj || 'itemdisplayname' in firstObj) {
              return obj as any[];
            }
            // also check nested arrays within this array
            for (const el of obj) {
              const found = findSiteDisplayArray(el);
              if (found) return found;
            }
          }
        }
        for (const key of Object.keys(obj)) {
          try {
            const val = obj[key];
            const found = findSiteDisplayArray(val);
            if (found) return found;
          } catch (err) {
            // ignore
          }
        }
        return null;
      };

      const fallback = findSiteDisplayArray(newData);
      if (Array.isArray(fallback) && fallback.length > 0) {
        setSiteDisplayOptions(fallback as any[]);
        // Try to find a selected id nearby: look for `selecteddisplayitemwiseid` in same level
        if (newData?.selecteddisplayitemwiseid != null) {
          setSelectedDisplayItemWiseId(Number(newData.selecteddisplayitemwiseid));
        } else if (Array.isArray(newData?.sitedisplayitemwisemodel) && newData.sitedisplayitemwisemodel.length > 0) {
          const first = newData.sitedisplayitemwisemodel[0];
          setSelectedDisplayItemWiseId(first?.selecteddisplayitemwiseid ?? 0);
        } else {
          setSelectedDisplayItemWiseId(0);
        }
      }
    }
  }, []);

    // Handler for site-display dropdown (uses MUI SelectChangeEvent)
    const handleSiteDisplayChange = useCallback((e: SelectChangeEvent<string | number>) => {
      setIsPublishButtonDisabled(false);
      const newVal = Number(e.target.value || 0);
      setSelectedDisplayItemWiseId(newVal);
    }, []);

  // ----------------------------------
  // Fetch Admin Configuration
  // ----------------------------------
  const fetchAdminConfiguration = useCallback(async () => {
    if (!params) {     
      return;
    }  
    try {
      setLoading(true);
      const response: any = await axiosInstance.post(
        `${BASE_URL}/api/v1/AdminConfiguration/GetAdminConfigurationDetailsBySite`,
        params
      );
      setLoading(false);    
      try {
        const data = response?.data?.data;       
        const json = JSON.stringify(data || {}, null, 2);      
      } catch (err) {
        console.log('could not stringify response.data.data', err);
      }
      if (response?.data?.responsecode === 200) {
        updateLocalStatesFromApi(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching admin config:', error);
      setLoading(false);
    }
  }, [params, updateLocalStatesFromApi]);

  useEffect(() => {
    fetchAdminConfiguration();
  }, [fetchAdminConfiguration]);


  const handleChangeOthers = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setIsPublishButtonDisabled(false);
    const { name, value } = e.target;
    const newValue = value === '' ? 0 : clampValueNoForce(name, value);
    if (newValue === undefined) {
      return;
    }
    setValues((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  }, []);

  const handleSwitchChange = useCallback((index: number) => {
    setIsPublishButtonDisabled(false);
    setMealtime((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, iscategoryapplicable: !item.iscategoryapplicable } : item
      )
    );
  }, []);

  // Reusable update callbacks
  const handleMenuProjection = useCallback(
    (index: number, fieldName: string, rawValue: number | string) => {
      validateAndUpdateField(
        index,
        fieldName,
        rawValue,
        setMenuProjectionData,
        setIsPublishButtonDisabled
      );
    },
    []
  );

  const handleMenuRequisition = useCallback(
    (index: number, fieldName: string, rawValue: number | string) => {
      validateAndUpdateField(
        index,
        fieldName,
        rawValue,
        setMenuRequisitionData,
        setIsPublishButtonDisabled
      );
    },
    []
  );

  const handleKitchenIntendent = useCallback(
    (index: number, fieldName: string, rawValue: number | string) => {
      validateAndUpdateField(
        index,
        fieldName,
        rawValue,
        setKitchenIntentData,
        setIsPublishButtonDisabled
      );
    },
    []
  );

  const handleExceptionalMenuRecognition = useCallback(
    (index: number, fieldName: string, rawValue: number | string) => {
      validateAndUpdateField(
        index,
        fieldName,
        rawValue,
        setExceptionalMenuRequisitionData,
        setIsPublishButtonDisabled
      );
    },
    []
  );

  const handleUpdateproductionplanmodel = useCallback(
    (index: number, fieldName: string, rawValue: number | string) => {
      validateAndUpdateField(
        index,
        fieldName,
        rawValue,
        setUpdateproductionplanmodel,
        setIsPublishButtonDisabled
      );
    },
    []
  );

  // ----------------------------------
  // Submit Handler: Normalize everything before submitting.
  // ----------------------------------
  const handleSubmit = useCallback(async () => {
    // Normalize flat form values.
    const normalizedValues = normalizeValues(values);

    // Normalize each array of objects.
    const normalizedMealtime = normalizeArray(mealtime);
    const normalizedMenuProjectionData = normalizeArray(menuProjectionData);
    const normalizedMenuRequisitionData = normalizeArray(menuRequisitionData);
    const normalizedExceptionalMenuRequisitionData = normalizeArray(exceptionalMenuRequisitionData);
    const normalizedKitchenIntentData = normalizeArray(kitchenIntentData);
    const normalizedUpdateproductionplanmodel = normalizeArray(updateproductionplanmodel);

    const payload = {
      initiatemenuplan: {
        reviewtimeperiodmonths: normalizedValues.reviewtimeperiodmonths,
        mealtimedetailslist: normalizedMealtime,
      },
      menuprojection: {
        pastmonthprojection: normalizedValues.pastmonthprojection,
        projectionquantitytolerancepercentage: normalizedValues.projectionquantitytolerancepercentage,
        projectioncomparedays: normalizedValues.projectioncomparedays,
        projectioncutoffrule: normalizedMenuProjectionData,
      },
      menurequisitionmodel: {
        pastmonthrequisition: normalizedValues.pastmonthrequisition,
        requisitionquantitytolerancepercentage: normalizedValues.requisitionquantitytolerancepercentage,
        requisitioncomparedays: normalizedValues.requisitioncomparedays,
        requisitioncutoffrule: normalizedMenuRequisitionData,
      },
      exceptionalmenurequisitionmodel: {
        pastmonthexceptionalrequisition: normalizedValues.pastmonthexceptionalrequisition,
        exceptionalrequisitionquantitytolerancepercentage: normalizedValues.exceptionalrequisitionquantitytolerancepercentage,
        exceptionalrequisitioncomparedays: normalizedValues.exceptionalrequisitioncomparedays,
        exceptionalrequisitioncutoffrule: normalizedExceptionalMenuRequisitionData,
      },
      menuprojectiondashboard: {
        pastmonthprojectiondashboard: normalizedValues.pastmonthprojectiondashboard,
      },
      kitchendashboard: {
        pastmonthkitchendashboard: normalizedValues.pastmonthkitchendashboard,
      },
      sitemanagerdashboard: {
        pastmonthsitemanagerdashboard: normalizedValues.pastmonthsitemanagerdashboard,
      },
      kitchenintentmodel: {
        kitchenintendcutoffrule: normalizedKitchenIntentData,
      },
      updateproductionplanmodel: {
        updateproductionplancutoffrule: normalizedUpdateproductionplanmodel,
      },     
   
      sitelist: params?.sitelist,
      userid,
      itemdisplaymasterid: selectedDisplayItemWiseId ?? 0,
    };

    try {
      setLoading(true);
      const response: any = await axiosInstance.post(
        `${BASE_URL}/api/v1/AdminConfiguration/SaveAdminConfigurationDetails`,
        payload
      );
      setLoading(false);
      if (response?.data?.responsecode === 200) {
        toast(`${response?.data.message}`, { type: "success" });
        updateLocalStatesFromApi(response.data.data);
        // Optionally, you can re-disable publish or show a success message.
      }
      else {
        toast(`${response?.data?.errormessage}`, { type: "error" });
      }
    } catch (error) {
      setLoading(false);
      toast("Error", { type: "error" });
      console.error('Error saving admin config:', error);

    }
  }, [
    values,
    mealtime,
    menuProjectionData,
    menuRequisitionData,
    exceptionalMenuRequisitionData,
    kitchenIntentData,
    updateproductionplanmodel,
    params?.sitelist,
    updateLocalStatesFromApi,
     selectedDisplayItemWiseId,
  ]);

  // ----------------------------------
  // Return everything needed
  // ----------------------------------
  return {
    loading,
    isPublishButtonDisabled,
    values,
    mealtime,
    menuProjectionData,
    menuRequisitionData,
    exceptionalMenuRequisitionData,
    kitchenIntentData,
    updateproductionplanmodel,
    siteDisplayOptions,
    selectedDisplayItemWiseId,
    handleChangeOthers,
    handleSwitchChange,
    handleMenuProjection,
    handleMenuRequisition,
    handleExceptionalMenuRecognition,
    handleKitchenIntendent,
    handleUpdateproductionplanmodel,
    handleSubmit,
    handleSiteDisplayChange,
  };
};

export default useAdminConfiguration;
